// src/components/WhatsAppFloat.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaWhatsapp, FaTimes } from 'react-icons/fa';
import { useLang } from '../context/LangContext';
import { WHATSAPP } from '../data/content';

const PHONE = '201228277068';

const WhatsAppFloat = () => {
  const { currentLang, isRtl } = useLang();
  const [isOpen,    setIsOpen   ] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const t = WHATSAPP[currentLang];

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
        setIsOpen(false);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handlePreset = useCallback((msg) => {
    window.open(`https://wa.me/${PHONE}?text=${encodeURIComponent(msg)}`, '_blank');
    setIsOpen(false);
  }, []);

  const toggleOpen = useCallback(() => setIsOpen(prev => !prev), []);

  // Memoize preset buttons to avoid re-creation
  const presetButtons = useMemo(() => t.presetMessages.map((msg, i) => (
    <motion.button
      key={i}
      whileHover={{ scale: 1.02, background: '#f0f0f0' }}
      whileTap={{ scale: 0.97 }}
      onClick={() => handlePreset(msg)}
      style={{
        textAlign  : isRtl ? 'right' : 'left',
        padding    : '10px 14px',
        background : '#fff',
        border     : 'none',
        borderRadius: '10px',
        cursor     : 'pointer',
        fontSize   : '0.82rem',
        color      : '#075E54',
        fontWeight : '600',
        boxShadow  : '0 1px 4px rgba(0,0,0,0.1)',
        width      : '100%',
      }}
    >
      {msg}
    </motion.button>
  )), [t.presetMessages, isRtl, handlePreset]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key="wa-float"
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.7 }}
          style={{
            position: 'fixed',
            bottom  : '25px',
            [isRtl ? 'left' : 'right']: '25px',
            zIndex  : 10000,
          }}
        >
          {/* Chat popup */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                key="chat-popup"
                initial={{ opacity: 0, scale: 0.85, y: 20 }}
                animate={{ opacity: 1, scale: 1,    y: -80 }}
                exit={{ opacity: 0, scale: 0.85,    y: 20  }}
                transition={{ type: 'spring', damping: 20 }}
                style={{
                  position    : 'absolute',
                  bottom      : 0,
                  [isRtl ? 'left' : 'right']: 0,
                  width       : 'min(300px, calc(100vw - 50px)',
                  background  : '#fff',
                  borderRadius: '20px',
                  boxShadow   : '0 20px 50px rgba(0,0,0,0.3)',
                  overflow    : 'hidden',
                  fontFamily  : 'Cairo, Poppins, sans-serif',
                }}
                dir={isRtl ? 'rtl' : 'ltr'}
              >
                {/* Header */}
                <div style={{ background: '#075E54', padding: '15px 18px', color: '#fff' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ position: 'relative' }}>
                      <FaWhatsapp size={30} />
                      <span style={{
                        position: 'absolute', bottom: 0, right: 0,
                        width: '10px', height: '10px',
                        background: '#4AD964', borderRadius: '50%',
                        border: '2px solid #075E54',
                      }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{t.supportName}</div>
                      <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>{t.status}</div>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div style={{ padding: '18px', background: '#e5ddd5' }}>
                  <div style={{
                    background: '#fff', padding: '12px', borderRadius: '10px 10px 10px 0',
                    marginBottom: '14px', fontSize: '0.85rem', color: '#444',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.05)', lineHeight: '1.5',
                  }}>
                    {t.message}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {presetButtons}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* FAB button */}
          <motion.button
            whileHover={{ scale: 1.12 }}
            whileTap={{ scale: 0.9 }}
            onClick={toggleOpen}
            aria-label={isOpen ? 'Close WhatsApp chat' : 'Open WhatsApp chat'}
            style={{
              width          : '62px',
              height         : '62px',
              borderRadius   : '50%',
              background     : '#25D366',
              border         : 'none',
              cursor         : 'pointer',
              display        : 'flex',
              alignItems     : 'center',
              justifyContent : 'center',
              boxShadow      : '0 10px 28px rgba(37,211,102,0.45)',
              position       : 'relative',
            }}
          >
            {/* Pulse ring - only when closed */}
            {!isOpen && (
              <motion.div
                animate={{ scale: [1, 1.45], opacity: [0.55, 0] }}
                transition={{ repeat: Infinity, duration: 2, ease: 'easeOut' }}
                style={{
                  position: 'absolute', inset: 0,
                  background: '#25D366', borderRadius: '50%', zIndex: -1,
                }}
              />
            )}

            {isOpen
              ? <FaTimes    size={22} color="#fff" />
              : <FaWhatsapp size={30} color="#fff" />
            }

            {/* Notification dot */}
            {!isOpen && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                style={{
                  position   : 'absolute',
                  top        : '-2px',
                  [isRtl ? 'left' : 'right']: '-2px',
                  width      : '20px', height: '20px',
                  background : '#FF3B30',
                  borderRadius: '50%',
                  border     : '2px solid #fff',
                  fontSize   : '10px',
                  color      : '#fff',
                  display    : 'flex',
                  alignItems : 'center',
                  justifyContent: 'center',
                  fontWeight : 'bold',
                }}
              >
                1
              </motion.span>
            )}
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WhatsAppFloat;
